import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  Upload, Download, Trash2, CheckCircle, Circle, FileText,
  Users, Search, FolderOpen, Building2, CreditCard, Save,
  ChevronDown, ChevronUp, AlertTriangle, ShieldCheck, Pencil,
} from "lucide-react";
import api from "../lib/api";

const REQUIRED_CATEGORIES = [
  { key: "photo", label: "Photo" },
  { key: "aadhar", label: "Aadhar Card" },
  { key: "pan", label: "PAN Card" },
  { key: "bank_details", label: "Bank Details (Passbook/Cheque)" },
  { key: "education_qualification", label: "Education Qualification" },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatFileSize = (bytes) => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const Documents = () => {
  const { currentBusiness, user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadCategory, setUploadCategory] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadNotes, setUploadNotes] = useState("");
  const [expandedEmp, setExpandedEmp] = useState(null);
  const [bankForm, setBankForm] = useState({ bank_account_no: "", bank_ifsc: "", bank_name: "", bank_branch: "" });
  const [savingBank, setSavingBank] = useState(false);
  const [editingBankDetails, setEditingBankDetails] = useState(false);

  // Check if user is HR/Admin
  const isHROrAdmin = user?.role && ['super_admin', 'business_admin', 'hr_manager'].includes(user.role);

  const fetchData = useCallback(async () => {
    if (!currentBusiness?.id) return;
    try {
      const [empRes, compRes] = await Promise.all([
        api.get(`/employees?business_id=${currentBusiness.id}`),
        api.get(`/documents/compliance?business_id=${currentBusiness.id}`),
      ]);
      setEmployees(empRes.data);
      setCompliance(compRes.data);
    } catch { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  }, [currentBusiness?.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchChecklist = async (empId) => {
    try {
      const res = await api.get(`/documents/checklist/${empId}`);
      setChecklist(res.data);
      setBankForm({
        bank_account_no: res.data.bank_details?.account_no || "",
        bank_ifsc: res.data.bank_details?.ifsc || "",
        bank_name: res.data.bank_details?.bank_name || "",
        bank_branch: res.data.bank_details?.branch || "",
      });
    } catch { toast.error("Failed to load checklist"); }
  };

  const handleSelectEmployee = (emp) => {
    setSelectedEmployee(emp);
    setExpandedEmp(emp.id);
    fetchChecklist(emp.id);
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadCategory) { toast.error("Select a file and category"); return; }
    const formData = new FormData();
    formData.append("file", uploadFile);
    formData.append("employee_id", selectedEmployee.id);
    formData.append("business_id", currentBusiness.id);
    formData.append("category", uploadCategory);
    if (uploadNotes) formData.append("notes", uploadNotes);
    try {
      const res = await api.post("/documents/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      if (res.data.warning) toast.warning(res.data.warning);
      else if (res.data.was_compressed) toast.success(res.data.compression_info || "Uploaded & compressed");
      else toast.success("Document uploaded");
      setUploadOpen(false);
      setUploadFile(null);
      setUploadNotes("");
      fetchChecklist(selectedEmployee.id);
      fetchData();
    } catch { toast.error("Upload failed"); }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await api.delete(`/documents/${docId}`);
      toast.success("Deleted");
      fetchChecklist(selectedEmployee.id);
      fetchData();
    } catch { toast.error("Delete failed"); }
  };

  const handleSaveBank = async () => {
    setSavingBank(true);
    try {
      await api.put(`/employees/${selectedEmployee.id}/bank-details`, bankForm);
      toast.success("Bank details saved");
      setEditingBankDetails(false);
      fetchChecklist(selectedEmployee.id);
      fetchData();
    } catch { toast.error("Failed to save bank details"); }
    finally { setSavingBank(false); }
  };

  const handleEditBankDetails = () => {
    // Pre-fill form with existing values
    setBankForm({
      bank_account_no: checklist.bank_details?.account_no || "",
      bank_ifsc: checklist.bank_details?.ifsc || "",
      bank_name: checklist.bank_details?.bank_name || "",
      bank_branch: checklist.bank_details?.branch || "",
    });
    setEditingBankDetails(true);
  };

  const handleCancelEditBank = () => {
    setEditingBankDetails(false);
    // Reset form to saved values
    setBankForm({
      bank_account_no: checklist.bank_details?.account_no || "",
      bank_ifsc: checklist.bank_details?.ifsc || "",
      bank_name: checklist.bank_details?.bank_name || "",
      bank_branch: checklist.bank_details?.branch || "",
    });
  };

  const downloadDoc = (docId) => {
    const token = localStorage.getItem("token");
    window.open(`${BACKEND_URL}/api/documents/${docId}/download?token=${token}`, "_blank");
  };

  const filteredCompliance = compliance?.employees?.filter(e =>
    e.employee_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (e.employee_code || "").toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (loading) {
    return <div className="flex justify-center py-20"><div className="animate-spin h-10 w-10 border-4 border-primary border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="documents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">Employee Documents</h1>
          <p className="text-slate-500">Manage mandatory documents for all employees</p>
        </div>
      </div>

      {/* Compliance Overview */}
      {compliance && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="stat-card">
            <CardContent className="p-0">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-blue-100"><Users className="h-6 w-6 text-blue-700" /></div>
                <div><p className="text-2xl font-bold text-slate-800">{compliance.total_employees}</p><p className="text-sm text-slate-500">Total Employees</p></div>
              </div>
            </CardContent>
          </Card>
          <Card className="stat-card">
            <CardContent className="p-0">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-green-100"><ShieldCheck className="h-6 w-6 text-green-700" /></div>
                <div><p className="text-2xl font-bold text-slate-800">{compliance.fully_compliant}</p><p className="text-sm text-slate-500">Fully Compliant</p></div>
              </div>
            </CardContent>
          </Card>
          <Card className="stat-card">
            <CardContent className="p-0">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl ${compliance.compliance_rate >= 80 ? 'bg-green-100' : compliance.compliance_rate >= 50 ? 'bg-amber-100' : 'bg-red-100'}`}>
                  <FileText className={`h-6 w-6 ${compliance.compliance_rate >= 80 ? 'text-green-700' : compliance.compliance_rate >= 50 ? 'text-amber-700' : 'text-red-700'}`} />
                </div>
                <div><p className="text-2xl font-bold text-slate-800">{compliance.compliance_rate}%</p><p className="text-sm text-slate-500">Compliance Rate</p></div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Search employees..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          className="pl-10 input-field" data-testid="doc-search"
        />
      </div>

      {/* Employee Document Table */}
      <Card className="card-soft" data-testid="compliance-table">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wide text-slate-400">Employee</TableHead>
                <TableHead className="text-xs uppercase tracking-wide text-slate-400">Code</TableHead>
                <TableHead className="text-xs uppercase tracking-wide text-slate-400">Documents</TableHead>
                <TableHead className="text-xs uppercase tracking-wide text-slate-400">Progress</TableHead>
                <TableHead className="text-xs uppercase tracking-wide text-slate-400">Bank</TableHead>
                <TableHead className="text-xs uppercase tracking-wide text-slate-400">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCompliance.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-500">No employees found</TableCell></TableRow>
              ) : (
                filteredCompliance.map(emp => (
                  <TableRow
                    key={emp.employee_id}
                    className="cursor-pointer hover:bg-primary/5 transition-colors"
                    onClick={() => handleSelectEmployee(employees.find(e => e.id === emp.employee_id) || emp)}
                    data-testid={`emp-doc-row-${emp.employee_id}`}
                  >
                    <TableCell className="font-medium text-slate-800">{emp.employee_name}</TableCell>
                    <TableCell className="text-slate-500 text-sm">{emp.employee_code || "—"}</TableCell>
                    <TableCell className="text-slate-600">{emp.uploaded_count}/{emp.total_count}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 min-w-[120px]">
                        <Progress value={emp.completion_percentage} className="h-2 flex-1" />
                        <span className="text-xs text-slate-500 w-8">{emp.completion_percentage}%</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {emp.has_bank_details ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200"><CheckCircle className="h-3 w-3 mr-1" /> Yes</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200"><AlertTriangle className="h-3 w-3 mr-1" /> No</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {emp.is_complete ? (
                        <Badge className="bg-green-500 text-white">Complete</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Incomplete</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Document Checklist Panel */}
      {selectedEmployee && checklist && (
        <Card className="card-soft border-2 border-primary/20" data-testid="document-checklist">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading text-lg">{checklist.employee_name}</CardTitle>
                <p className="text-sm text-slate-500 mt-0.5">
                  {checklist.uploaded_count}/{checklist.total_count} documents uploaded ({checklist.completion_percentage}%)
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Progress value={checklist.completion_percentage} className="h-2.5 w-32" />
                {checklist.is_complete ? (
                  <Badge className="bg-green-500 text-white">All Complete</Badge>
                ) : (
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                    {checklist.total_count - checklist.uploaded_count} missing
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Document Checklist Items */}
            {checklist.checklist.map((item) => (
              <div
                key={item.key}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                  item.uploaded ? "bg-green-50/50 border-green-200" : "bg-white border-slate-200"
                }`}
                data-testid={`checklist-item-${item.key}`}
              >
                <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                  item.uploaded ? "bg-green-500 text-white" : "border-2 border-slate-300"
                }`}>
                  {item.uploaded ? <CheckCircle className="h-4 w-4" /> : <Circle className="h-4 w-4 text-slate-300" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${item.uploaded ? "text-green-700" : "text-slate-800"}`}>{item.label}</p>
                  {item.documents?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-1">
                      {item.documents.map(doc => (
                        <div key={doc.id} className="flex items-center gap-1 text-xs text-slate-500 bg-slate-100 rounded px-2 py-0.5">
                          <FileText className="h-3 w-3" />
                          <span className="max-w-[120px] truncate">{doc.filename}</span>
                          <span className="text-slate-400">({formatFileSize(doc.file_size)})</span>
                          <button onClick={(e) => { e.stopPropagation(); downloadDoc(doc.id); }} className="text-primary hover:text-primary/80">
                            <Download className="h-3 w-3" />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); handleDelete(doc.id); }} className="text-red-500 hover:text-red-700">
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {!item.uploaded && (
                  <Button
                    size="sm" variant="outline" className="flex-shrink-0 text-xs"
                    onClick={() => { setUploadCategory(item.key); setUploadOpen(true); }}
                    data-testid={`upload-btn-${item.key}`}
                  >
                    <Upload className="h-3 w-3 mr-1" /> Upload
                  </Button>
                )}
                {item.uploaded && (
                  <Button
                    size="sm" variant="ghost" className="flex-shrink-0 text-xs text-slate-400"
                    onClick={() => { setUploadCategory(item.key); setUploadOpen(true); }}
                  >
                    <Upload className="h-3 w-3 mr-1" /> Replace
                  </Button>
                )}
              </div>
            ))}

            {/* Bank Details Form */}
            <div className={`p-4 rounded-lg border transition-all ${
              checklist.bank_details?.has_details ? "bg-green-50/50 border-green-200" : "bg-white border-slate-200"
            }`} data-testid="bank-details-section">
              <div className="flex items-center gap-3 mb-3">
                <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                  checklist.bank_details?.has_details ? "bg-green-500 text-white" : "border-2 border-slate-300"
                }`}>
                  {checklist.bank_details?.has_details ? <CheckCircle className="h-4 w-4" /> : <CreditCard className="h-4 w-4 text-slate-300" />}
                </div>
                <p className={`text-sm font-medium ${checklist.bank_details?.has_details ? "text-green-700" : "text-slate-800"}`}>
                  Bank Account Details
                </p>
                {checklist.bank_details?.has_details && !editingBankDetails && (
                  <div className="ml-auto flex items-center gap-2">
                    {isHROrAdmin && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleEditBankDetails}
                        className="h-7 px-2 text-xs border-blue-200 text-blue-700 hover:bg-blue-50"
                        data-testid="edit-bank-btn"
                      >
                        <Pencil className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                    )}
                    <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300 text-xs">
                      <ShieldCheck className="h-3 w-3 mr-1" />
                      Secured
                    </Badge>
                  </div>
                )}
              </div>
              
              {checklist.bank_details?.has_details && !editingBankDetails ? (
                /* Show masked bank details when already saved and not editing */
                <div className="ml-9 space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Account Number</Label>
                      <div className="h-9 px-3 py-2 bg-slate-100 rounded-lg text-sm text-slate-600 flex items-center">
                        {checklist.bank_details.account_no ? 
                          `****${checklist.bank_details.account_no.slice(-4)}` : 
                          "****"
                        }
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">IFSC Code</Label>
                      <div className="h-9 px-3 py-2 bg-slate-100 rounded-lg text-sm text-slate-600 flex items-center">
                        {checklist.bank_details.ifsc || "—"}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Bank Name</Label>
                      <div className="h-9 px-3 py-2 bg-slate-100 rounded-lg text-sm text-slate-600 flex items-center">
                        {checklist.bank_details.bank_name || "—"}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Branch</Label>
                      <div className="h-9 px-3 py-2 bg-slate-100 rounded-lg text-sm text-slate-600 flex items-center">
                        {checklist.bank_details.branch || "—"}
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-green-600 flex items-center gap-1 mt-2">
                    <ShieldCheck className="h-3 w-3" />
                    Bank details are securely saved.{!isHROrAdmin && " Contact HR to update."}
                  </p>
                </div>
              ) : (
                /* Show editable form when not yet saved OR when HR is editing */
                <>
                  {editingBankDetails && (
                    <p className="text-xs text-blue-600 ml-9 mb-2 flex items-center gap-1">
                      <Pencil className="h-3 w-3" />
                      Editing bank details for {selectedEmployee?.first_name} {selectedEmployee?.last_name}
                    </p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 ml-9">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Account Number</Label>
                      <Input value={bankForm.bank_account_no} onChange={e => setBankForm(p => ({ ...p, bank_account_no: e.target.value }))}
                        className="input-field h-9 text-sm" placeholder="1234567890" data-testid="bank-account-input" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">IFSC Code</Label>
                      <Input value={bankForm.bank_ifsc} onChange={e => setBankForm(p => ({ ...p, bank_ifsc: e.target.value }))}
                        className="input-field h-9 text-sm" placeholder="SBIN0001234" data-testid="bank-ifsc-input" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Bank Name</Label>
                      <Input value={bankForm.bank_name} onChange={e => setBankForm(p => ({ ...p, bank_name: e.target.value }))}
                        className="input-field h-9 text-sm" placeholder="State Bank of India" data-testid="bank-name-input" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Branch</Label>
                      <Input value={bankForm.bank_branch} onChange={e => setBankForm(p => ({ ...p, bank_branch: e.target.value }))}
                        className="input-field h-9 text-sm" placeholder="Main Branch" data-testid="bank-branch-input" />
                    </div>
                  </div>
                  <div className="ml-9 mt-3 flex gap-2">
                    {editingBankDetails && (
                      <Button variant="outline" size="sm" className="text-xs" onClick={handleCancelEditBank}>
                        Cancel
                      </Button>
                    )}
                    <Button size="sm" className="btn-primary text-xs" onClick={handleSaveBank} disabled={savingBank} data-testid="save-bank-btn">
                      <Save className="h-3 w-3 mr-1" /> {savingBank ? "Saving..." : "Save Bank Details"}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-md" onCloseAutoFocus={e => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-heading">Upload Document</DialogTitle>
            <DialogDescription>
              Upload {REQUIRED_CATEGORIES.find(c => c.key === uploadCategory)?.label || "document"} for {selectedEmployee?.first_name} {selectedEmployee?.last_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={uploadCategory} onValueChange={setUploadCategory}>
                <SelectTrigger className="input-field" data-testid="upload-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REQUIRED_CATEGORIES.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>File (auto-compressed, 2 MB limit per employee)</Label>
              <Input type="file" onChange={e => setUploadFile(e.target.files[0])} className="input-field" data-testid="upload-file-input" />
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea value={uploadNotes} onChange={e => setUploadNotes(e.target.value)}
                placeholder="Any notes..." className="input-field min-h-[60px]" data-testid="upload-notes" />
            </div>
            <Button onClick={handleUpload} disabled={!uploadFile} className="w-full btn-primary" data-testid="upload-submit-btn">
              <Upload className="h-4 w-4 mr-2" /> Upload Document
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Documents;
